/**
 * Expense Service
 * Handles expense tracking and management
 */

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  getDocs,
  getDoc,
  serverTimestamp,
  onSnapshot,
  Timestamp,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from './firebase';

/**
 * Expense categories
 */
export const EXPENSE_CATEGORIES = [
  'Travel',
  'Meals & Entertainment',
  'Office Supplies',
  'Software & Subscriptions',
  'Equipment',
  'Marketing & Advertising',
  'Professional Services',
  'Utilities',
  'Rent',
  'Insurance',
  'Taxes & Licenses',
  'Bank Fees',
  'Education & Training',
  'Other',
];

/**
 * Create a new expense
 */
export const createExpense = async (userId, expenseData) => {
  try {
    const expense = {
      userId,
      category: expenseData.category,
      amount: expenseData.amount,
      date: new Date(expenseData.date),
      description: expenseData.description,
      vendor: expenseData.vendor || '',
      paymentMethod: expenseData.paymentMethod || '',
      receiptUrl: expenseData.receiptUrl || '',
      clientId: expenseData.clientId || null,
      projectId: expenseData.projectId || null,
      tags: expenseData.tags || [],
      notes: expenseData.notes || '',
      billable: expenseData.billable || false,
      reimbursable: expenseData.reimbursable || false,
      status: 'recorded', // recorded, reimbursed, billed
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    const docRef = await addDoc(collection(db, 'expenses'), expense);
    return { expenseId: docRef.id, ...expense };
  } catch (error) {
    console.error('Error creating expense:', error);
    throw error;
  }
};

/**
 * Update an expense
 */
export const updateExpense = async (expenseId, updateData) => {
  try {
    const expenseRef = doc(db, 'expenses', expenseId);
    await updateDoc(expenseRef, {
      ...updateData,
      updatedAt: serverTimestamp(),
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating expense:', error);
    throw error;
  }
};

/**
 * Delete an expense
 */
export const deleteExpense = async (expenseId) => {
  try {
    const expenseRef = doc(db, 'expenses', expenseId);
    await deleteDoc(expenseRef);
    return { success: true };
  } catch (error) {
    console.error('Error deleting expense:', error);
    throw error;
  }
};

/**
 * Get a single expense
 */
export const getExpense = async (expenseId) => {
  try {
    const expenseRef = doc(db, 'expenses', expenseId);
    const expenseDoc = await getDoc(expenseRef);

    if (!expenseDoc.exists()) {
      throw new Error('Expense not found');
    }

    return { id: expenseDoc.id, ...expenseDoc.data() };
  } catch (error) {
    console.error('Error getting expense:', error);
    throw error;
  }
};

/**
 * Get all expenses for a user
 */
export const getExpenses = async (userId, filters = {}) => {
  try {
    let q = query(
      collection(db, 'expenses'),
      where('userId', '==', userId),
      orderBy('date', 'desc')
    );

    if (filters.category) {
      q = query(q, where('category', '==', filters.category));
    }

    if (filters.clientId) {
      q = query(q, where('clientId', '==', filters.clientId));
    }

    if (filters.projectId) {
      q = query(q, where('projectId', '==', filters.projectId));
    }

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting expenses:', error);
    throw error;
  }
};

/**
 * Subscribe to expenses changes
 */
export const subscribeToExpenses = (userId, callback, filters = {}) => {
  let q = query(
    collection(db, 'expenses'),
    where('userId', '==', userId),
    orderBy('date', 'desc')
  );

  if (filters.category) {
    q = query(q, where('category', '==', filters.category));
  }

  return onSnapshot(q, (snapshot) => {
    const expenses = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    callback(expenses);
  });
};

/**
 * Get expense statistics
 */
export const getExpenseStats = async (userId, startDate, endDate) => {
  try {
    const q = query(
      collection(db, 'expenses'),
      where('userId', '==', userId),
      where('date', '>=', startDate),
      where('date', '<=', endDate)
    );

    const snapshot = await getDocs(q);
    const expenses = snapshot.docs.map(doc => doc.data());

    const totalExpenses = expenses.reduce((sum, exp) => sum + exp.amount, 0);
    const byCategory = {};
    const byMonth = {};

    expenses.forEach(exp => {
      // By category
      if (!byCategory[exp.category]) {
        byCategory[exp.category] = 0;
      }
      byCategory[exp.category] += exp.amount;

      // By month
      const month = exp.date.toDate().toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
      if (!byMonth[month]) {
        byMonth[month] = 0;
      }
      byMonth[month] += exp.amount;
    });

    return {
      totalExpenses,
      expenseCount: expenses.length,
      byCategory,
      byMonth,
      billableExpenses: expenses.filter(e => e.billable).reduce((sum, e) => sum + e.amount, 0),
      reimbursableExpenses: expenses.filter(e => e.reimbursable).reduce((sum, e) => sum + e.amount, 0),
    };
  } catch (error) {
    console.error('Error getting expense stats:', error);
    throw error;
  }
};

/**
 * Upload receipt image to Firebase Storage
 * @param {string} userId - User ID
 * @param {File} file - File to upload
 * @returns {Promise<string>} Download URL
 */
export const uploadReceipt = async (userId, file) => {
  if (!file) throw new Error('No file provided');

  // Validate file type
  const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'application/pdf'];
  if (!allowedTypes.includes(file.type)) {
    throw new Error('File type not supported. Use JPEG, PNG, WebP, HEIC, or PDF.');
  }

  // Validate file size (max 10MB)
  if (file.size > 10 * 1024 * 1024) {
    throw new Error('File must be less than 10MB');
  }

  const ext = file.name.split('.').pop() || 'jpg';
  const filename = `receipts/${userId}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
  const storageRef = ref(storage, filename);

  await uploadBytes(storageRef, file);
  return await getDownloadURL(storageRef);
};

/**
 * Delete receipt from Firebase Storage
 * @param {string} receiptUrl - Receipt URL to delete
 */
export const deleteReceipt = async (receiptUrl) => {
  if (!receiptUrl) return;

  try {
    const storageRef = ref(storage, receiptUrl);
    await deleteObject(storageRef);
  } catch (error) {
    // Ignore errors if file doesn't exist
    console.warn('Could not delete receipt file:', error.message);
  }
};

/**
 * Get expenses filtered by date range
 * @param {string} userId - User ID
 * @param {Date} startDate - Start date
 * @param {Date} endDate - End date
 * @param {Object} filters - Additional filters
 * @returns {Promise<Array>} Expenses
 */
export const getExpensesByDateRange = async (userId, startDate, endDate, filters = {}) => {
  try {
    let q = query(
      collection(db, 'expenses'),
      where('userId', '==', userId),
      where('date', '>=', Timestamp.fromDate(startDate)),
      where('date', '<=', Timestamp.fromDate(endDate)),
      orderBy('date', 'desc')
    );

    const snapshot = await getDocs(q);
    let expenses = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    // Apply additional filters client-side
    if (filters.category) {
      expenses = expenses.filter(e => e.category === filters.category);
    }
    if (filters.billable !== undefined) {
      expenses = expenses.filter(e => e.billable === filters.billable);
    }
    if (filters.reimbursable !== undefined) {
      expenses = expenses.filter(e => e.reimbursable === filters.reimbursable);
    }

    return expenses;
  } catch (error) {
    console.error('Error getting expenses by date range:', error);
    throw error;
  }
};

/**
 * Export expenses to CSV string
 * @param {Array} expenses - Array of expense objects
 * @returns {string} CSV content
 */
export const exportExpensesToCSV = (expenses) => {
  const headers = [
    'Date',
    'Category',
    'Description',
    'Vendor',
    'Amount',
    'Payment Method',
    'Billable',
    'Reimbursable',
    'Status',
    'Notes',
    'Receipt',
  ];

  const rows = expenses.map(exp => {
    const date = exp.date?.toDate ? exp.date.toDate() : new Date(exp.date);
    return [
      date.toLocaleDateString('en-US'),
      exp.category || '',
      `"${(exp.description || '').replace(/"/g, '""')}"`,
      `"${(exp.vendor || '').replace(/"/g, '""')}"`,
      (exp.amount || 0).toFixed(2),
      exp.paymentMethod || '',
      exp.billable ? 'Yes' : 'No',
      exp.reimbursable ? 'Yes' : 'No',
      exp.status || 'recorded',
      `"${(exp.notes || '').replace(/"/g, '""')}"`,
      exp.receiptUrl ? 'Yes' : 'No',
    ];
  });

  return [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
};
